import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CircleHelp, X, ChevronDown, BookOpen, Loader2 } from 'lucide-react';
import helpApi from '../../api/help';

// Route → Help-Key Mapping (Fallback wenn API nicht erreichbar)
const ROUTE_MAP = {
  '/': 'dashboard',
  '/dna': 'dna',
  '/blog': 'blog',
  '/website': 'webseite',
  '/media': 'medien',
  '/seo': 'seo',
  '/keywords': 'keywords',
  '/contact-submissions': 'anfragen',
  '/settings': 'einstellungen',
};

export default function HelpDrawer() {
  const [open, setOpen] = useState(false);
  const [helpData, setHelpData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const drawerRef = useRef(null);
  const location = useLocation();

  // Help-Key aus der aktuellen Route ermitteln
  const getPageKey = () => {
    const path = location.pathname;
    // Exakter Match
    if (ROUTE_MAP[path]) return ROUTE_MAP[path];
    // Prefix-Match (z.B. /website/123 → webseite)
    for (const [route, key] of Object.entries(ROUTE_MAP)) {
      if (route !== '/' && path.startsWith(route)) return key;
    }
    return 'dashboard';
  };

  // Hilfe laden wenn Drawer geoeffnet wird oder Route wechselt
  useEffect(() => {
    if (!open) return;

    const pageKey = getPageKey();
    setLoading(true);
    setError(null);

    helpApi.getPage(pageKey)
      .then(res => {
        setHelpData(res.data || res);
        setExpandedSections({});
      })
      .catch(() => {
        setError('Hilfe konnte nicht geladen werden.');
        setHelpData(null);
      })
      .finally(() => setLoading(false));
  }, [open, location.pathname]);

  // Klick ausserhalb schliesst den Drawer
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ESC schliesst den Drawer
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const toggleSection = (idx) => {
    setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`
          fixed bottom-4 right-4 z-40
          w-9 h-9 rounded-full shadow-md opacity-50 hover:opacity-100
          flex items-center justify-center
          transition-all duration-300
          ${open
            ? 'bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300 scale-90'
            : 'bg-primary-600 text-white hover:bg-primary-700 hover:scale-105'
          }
        `}
        title="Hilfe"
      >
        {open ? <X size={16} /> : <CircleHelp size={16} />}
      </button>

      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] transition-opacity" />
      )}

      {/* Drawer Panel */}
      <div
        ref={drawerRef}
        className={`
          fixed top-0 right-0 z-50
          h-full w-96 max-w-[90vw]
          bg-white dark:bg-slate-800
          shadow-2xl
          transform transition-transform duration-300 ease-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-primary-50 to-white dark:from-primary-900/20 dark:to-slate-800">
          <div className="w-9 h-9 rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
            <BookOpen size={18} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {helpData?.title || 'Hilfe'}
            </h2>
            <p className="text-[11px] text-gray-500 dark:text-slate-400">
              Kontextsensitive Hilfe
            </p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-primary-500" />
            </div>
          )}

          {error && (
            <div className="p-5 text-center">
              <p className="text-sm text-gray-500 dark:text-slate-400">{error}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
                Hilfe-Inhalte werden nach dem naechsten Update verfuegbar sein.
              </p>
            </div>
          )}

          {!loading && !error && helpData?.sections?.length > 0 && (
            <div className="divide-y divide-gray-100 dark:divide-slate-700">
              {helpData.sections.map((section, idx) => (
                <div key={idx}>
                  <button
                    onClick={() => toggleSection(idx)}
                    className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-slate-750 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-800 dark:text-slate-200">
                      {section.heading}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${
                        expandedSections[idx] ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedSections[idx] && (
                    <div className="px-5 pb-4 text-sm text-gray-600 dark:text-slate-300 leading-relaxed prose-sm">
                      {section.body.split('\n').map((line, i) => {
                        if (!line.trim()) return <br key={i} />;
                        if (line.startsWith('- ')) {
                          return (
                            <div key={i} className="flex gap-2 ml-1 my-0.5">
                              <span className="text-primary-500 mt-0.5">•</span>
                              <span>{line.slice(2)}</span>
                            </div>
                          );
                        }
                        if (line.startsWith('**') && line.endsWith('**')) {
                          return <p key={i} className="font-semibold text-gray-800 dark:text-slate-200 mt-2">{line.slice(2, -2)}</p>;
                        }
                        return <p key={i} className="my-0.5">{line}</p>;
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!loading && !error && (!helpData || helpData.sections?.length === 0) && (
            <div className="p-5 text-center py-16">
              <BookOpen size={32} className="mx-auto text-gray-300 dark:text-slate-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-slate-400">
                Fuer diese Seite ist noch keine Hilfe verfuegbar.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80">
          <p className="text-[10px] text-gray-400 dark:text-slate-500 text-center">
            Tastenkuerzel: <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-slate-700 rounded text-[9px]">?</kbd> zum Oeffnen
          </p>
        </div>
      </div>
    </>
  );
}
